/**
 * DMARC Guardian -- Editorial Grid Design Tokens (Concept 7)
 *
 * Single source of truth for all visual primitives.
 * Sage green / forest palette with Fraunces serif headings.
 */

// ─── Color Palette ──────────────────────────────────────────────
export const colors = {
  // Backgrounds
  bg: {
    DEFAULT: '#E8ECE6', // sage green tinted cream
    deep: '#D9DFD5', // darker sage for contrast zones
    surface: '#F4F6F2', // card backgrounds
    elevated: '#FFFFFF', // hover / elevated cards
  },

  // Borders
  border: {
    DEFAULT: '#C5CCC2', // standard border
    light: '#D9DFD5', // lighter, section dividers
  },

  // Primary -- deep forest green
  primary: {
    DEFAULT: '#1A3A2A',
    light: '#2D5F42',
    soft: 'rgba(26,58,42,0.06)',
    softBorder: 'rgba(26,58,42,0.15)',
  },

  // Accent -- burnt orange
  accent: {
    DEFAULT: '#D4622B',
    hover: '#9A4318',
    soft: 'rgba(212,98,43,0.07)',
    softBorder: 'rgba(212,98,43,0.2)',
  },

  // Semantic
  success: '#2D7A4F',
  danger: '#B33A3A',
  amber: '#C48B1A',

  // Text
  text: {
    DEFAULT: '#1A1F1C',
    secondary: '#5A665E',
    muted: '#8A958D',
  },

  // Terminal (dark panel)
  terminal: {
    bg: '#1A1F1C',
    chrome: '#252B28',
    chromeBorder: '#2E3531',
    text: '#C5E4D0',
    muted: '#5A665E',
  },

  // Traffic-light dots (terminal chrome)
  dot: {
    red: '#E5534B',
    yellow: '#D4A72C',
    green: '#3FB950',
  },

  // Code block (inline)
  code: {
    bg: '#1A1F1C',
    text: '#C5E4D0',
  },
} as const

// ─── Typography ──────────────────────────────────────────────────
export const fontFamily = {
  heading: ['Fraunces', 'Georgia', 'serif'],
  body: ['General Sans', '-apple-system', 'sans-serif'],
  mono: ['JetBrains Mono', 'monospace'],
} as const

export const fontSize = {
  '2xs': '11px',
  xs: '12px',
  sm: '13px',
  base: '14px',
  md: '15px',
  lg: '16px',
  xl: '17px',
  '2xl': '20px',
  '3xl': '22px',
  '4xl': '28px',
  '5xl': '42px',
  '6xl': '48px',
  '7xl': '56px',
} as const

export const fontWeight = {
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  black: '900',
} as const

// ─── Spacing ─────────────────────────────────────────────────────
export const spacing = {
  px: '1px',
  0: '0',
  0.5: '2px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  10: '40px',
  12: '48px',
  14: '56px',
  16: '64px',
  20: '80px',
  24: '96px',
  25: '100px',
} as const

// ─── Border Radius ───────────────────────────────────────────────
export const borderRadius = {
  sm: '4px',
  DEFAULT: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
} as const

// ─── Shadows ─────────────────────────────────────────────────────
export const boxShadow = {
  sm: '0 1px 3px rgba(0,0,0,0.04)',
  DEFAULT: '0 1px 12px rgba(0,0,0,0.06)',
  md: '0 4px 16px rgba(0,0,0,0.06)',
  lg: '0 12px 32px rgba(0,0,0,0.06)',
  xl: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
  card: '0 8px 32px rgba(26,58,42,0.1)',
  nav: '0 1px 12px rgba(0,0,0,0.06)',
} as const

// ─── Transitions ─────────────────────────────────────────────────
export const transition = {
  fast: '150ms',
  DEFAULT: '200ms',
  medium: '300ms',
  slow: '600ms',
} as const

// ─── Breakpoints ─────────────────────────────────────────────────
export const screens = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1200px',
} as const

// ─── Z-Index ─────────────────────────────────────────────────────
export const zIndex = {
  background: '0',
  grain: '1',
  content: '2',
  nav: '100',
} as const

// ─── Layout ──────────────────────────────────────────────────────
export const layout = {
  maxWidth: '1200px',
  navHeight: '64px',
  sectionPadding: '100px 48px',
  sectionPaddingMobile: '60px 24px',
} as const
